import { z } from "zod";
import {
  isAllowedHandlerUrl,
  isPublicHttpUrl,
  HANDLER_URL_ERROR,
  PUBLIC_HTTP_URL_ERROR,
} from "../utils/safe-url";
import { validateContextPublicUrls } from "./context-urls";
import type { JSONSchema7 } from "./json-schema";

/**
 * Extended JSONSchema7 type that includes RJSF extensions like enumNames
 */
export type ExtendedJSONSchema7 = JSONSchema7 & {
  enumNames?: string[];
  [key: string]: unknown;
};

/**
 * UI Schema for RJSF form customization
 */
export type UiSchema = {
  "ui:widget"?: string;
  "ui:title"?: string;
  "ui:description"?: string;
  "ui:placeholder"?: string;
  "ui:options"?: Record<string, unknown>;
  [key: string]: unknown;
};

// Helper schemas
export const safeUrlSchema = z.string().refine((url) => isPublicHttpUrl(url), {
  message: PUBLIC_HTTP_URL_ERROR,
});

const handlerUrlSchema = z.string().refine((url) => isAllowedHandlerUrl(url), {
  message: HANDLER_URL_ERROR,
});

// JSONSchema7 validation
const jsonSchema7Schema = z.custom<ExtendedJSONSchema7>(
  (val) => typeof val === "object" && val !== null,
  { message: "Must be a valid JSON Schema object" }
);

// UiSchema validation
const uiSchemaSchema = z.custom<UiSchema>((val) => typeof val === "object" && val !== null, {
  message: "Must be a valid UiSchema object",
});

// Handler schemas (per-action handlers for webhooks, triggers, etc.)
const webhookHandlerSchema = z.object({
  type: z.literal("webhook"),
  url: handlerUrlSchema,
  headers: z.record(z.string(), z.string()),
});

const triggerHandlerSchema = webhookHandlerSchema.extend({
  type: z.literal("trigger"),
  tokenId: z.string().min(1),
});

const handlerSchema = z.discriminatedUnion("type", [webhookHandlerSchema, triggerHandlerSchema]);

/** Action config without handlers — used for chat widgets and agent tool input. */
export const taskActionInputSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  description: z.string().optional(),
  schema: jsonSchema7Schema.optional(),
  ui: uiSchemaSchema.optional(),
  data: z.record(z.string(), z.unknown()).optional(),
});

// Action schema with JSONSchema-based feedback and optional handlers
const taskActionSchema = taskActionInputSchema.extend({
  // Optional handlers for this action - if present, must have at least 1
  handlers: z.array(handlerSchema).min(1).optional(),
});

// UI Field Schema
const uiFieldSchemaSchema: z.ZodType<Record<string, unknown>> = z
  .object({
    "ui:widget": z.string().optional(),
    "ui:title": z.string().optional(),
    "ui:description": z.string().optional(),
    "ui:options": z.record(z.string(), z.unknown()).optional(),
    items: z.lazy(() => z.record(z.string(), uiFieldSchemaSchema)).optional(),
  })
  .passthrough();

// Context UI Schema
const contextUiSchema = z.record(z.string(), uiFieldSchemaSchema).optional();

// Context data schema
const contextDataSchema = z
  .object({
    data: z.record(z.string(), z.unknown()),
    ui: contextUiSchema,
  })
  .optional();

export const TASK_CONTEXT_FORMAT_VERSION = 2 as const;
export type TaskContextFormatVersion = typeof TASK_CONTEXT_FORMAT_VERSION;

// Main TaskContext schema
const taskContextObjectBaseSchema = z.object({
  /** Source application id; omitted tasks are grouped in the default inbox. */
  app: z.string().min(1).optional(),
  type: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  validUntil: z.string().optional(),
  context: contextDataSchema,
  /** Task context wire format version. @default 2 */
  contextVersion: z.literal(2).optional(),
  /** @deprecated Use `contextVersion`. Accepted on ingest only. */
  version: z.literal(2).optional(),
  actions: z.array(taskActionSchema).min(1, "At least one action is required"),
});

function normalizeTaskContextVersion<T extends { contextVersion?: 2; version?: 2 }>(
  data: T
): Omit<T, "version" | "contextVersion"> & { contextVersion: 2 } {
  const { version: legacyVersion, contextVersion, ...rest } = data;
  return {
    ...rest,
    contextVersion: contextVersion ?? legacyVersion ?? TASK_CONTEXT_FORMAT_VERSION,
  };
}

const taskContextObjectSchema = taskContextObjectBaseSchema.transform(
  normalizeTaskContextVersion
);

function refineContextPublicUrls<T extends { context?: z.infer<typeof contextDataSchema> }>(
  data: T,
  ctx: z.RefinementCtx
) {
  const error = validateContextPublicUrls(data.context);
  if (error) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: error,
      path: ["context"],
    });
  }
}

export const taskContextSchema = taskContextObjectSchema.superRefine(
  refineContextPublicUrls
);

/**
 * Assignment targets at task create (not stored in task context JSON).
 * Unknown user emails are auto-provisioned as assignee memberships (count toward seat limits).
 *
 * Virtual group slugs (membership derived from tenantUsers, not groupMembers):
 * - `all` — all workspace members (omit assignTo defaults to this)
 * - `admins` — workspace users with tenant admin role
 */
export const VIRTUAL_ASSIGN_TO_GROUP_SLUGS = ["all", "admins"] as const;

export const assignToSchema = z
  .object({
    users: z.array(z.string().email()).optional(),
    groups: z.array(z.string().min(1)).optional(),
  })
  .refine(
    (data) => {
      const groups = data.groups ?? [];
      if (groups.includes("all") && groups.length > 1) {
        return false;
      }
      return true;
    },
    { message: 'Cannot combine "all" with other group slugs' }
  );

/** A short thread-scoped status update message (1-2 sentences). */
export const threadUpdateMessageSchema = z.string().min(1).max(500);

/**
 * Lifecycle status carried by a thread update. Drives the icon and color shown
 * in the inbox status bar. Defaults to `info` when omitted.
 */
export const threadUpdateStatuses = [
  "info",
  "queued",
  "running",
  "waiting",
  "succeeded",
  "failed",
  "cancelled",
] as const;

export const threadUpdateStatusSchema = z.enum(threadUpdateStatuses);

/** The default status applied when an update omits one. */
export const DEFAULT_THREAD_UPDATE_STATUS: ThreadUpdateStatus = "info";

/** Shared shape for a thread update (standalone and at task creation). */
export const threadUpdateInputSchema = z.object({
  message: threadUpdateMessageSchema,
  status: threadUpdateStatusSchema.optional(),
});

/** Thread priority levels for inbox ordering and display. */
export const taskPriorities = ["low", "normal", "high", "urgent"] as const;

export const taskPrioritySchema = z.enum(taskPriorities);

export type TaskPriority = (typeof taskPriorities)[number];

export const DEFAULT_TASK_PRIORITY: TaskPriority = "normal";

export const LOWEST_TASK_PRIORITY: TaskPriority = "low";

export const TASK_PRIORITY_RANK: Record<TaskPriority, number> = {
  low: 1,
  normal: 2,
  high: 3,
  urgent: 4,
};

/**
 * Agent release at task create (not shown in inbox reviewer UI).
 * Used for feedback analysis over deploys.
 */
export const agentTelemetrySchema = z.object({
  /** Agent or app version (semver, git SHA, deploy tag — caller-defined). */
  version: z.string().min(1),
});

/** POST /v1 body: task context plus optional assignment. */
export const createTaskBodySchema = taskContextObjectBaseSchema
  .extend({
    assignTo: assignToSchema.optional(),
    /**
     * Groups related tasks together. When omitted, the server generates one and
     * returns it so the caller can reuse it on later tasks in the same thread.
     */
    threadId: z.string().min(1).optional(),
    /**
     * Optional thread priority. When set, applies to the whole thread and
     * overwrites any previous priority. Omit on later tasks to leave unchanged.
     */
    priority: taskPrioritySchema.optional(),
    /**
     * Optional initial status update logged against the task's thread. Shows in
     * the inbox status bar and the thread update log.
     */
    update: threadUpdateInputSchema.optional(),
    /** Agent release version — not shown in inbox UI. */
    agent: agentTelemetrySchema.optional(),
  })
  .transform(normalizeTaskContextVersion)
  .superRefine(refineContextPublicUrls);

/** POST /v1/threads/:threadId/updates body: a standalone thread update. */
export const threadUpdateBodySchema = threadUpdateInputSchema;

/** Action widget config for a seeded assistant message (mirrors the `requestActionInput` tool input). */
export const agentChatSeedActionSchema = z.object({
  /** Stable action id echoed back on submit; auto-derived from title when omitted. */
  id: z.string().min(1).optional(),
  title: z.string().min(1),
  description: z.string().optional(),
  /** JSON Schema object for the form fields. */
  schema: z.record(z.string(), z.unknown()).optional(),
  /** RJSF ui schema overrides (ui:widget, ui:enumNames, etc.). */
  ui: z.record(z.string(), z.unknown()).optional(),
  /** Optional default field values. */
  data: z.record(z.string(), z.unknown()).optional(),
});

/** A single pre-seeded chat message injected into a programmatic agent chat. */
export const agentChatSeedMessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  text: z.string().min(1),
  /** Optional display-name override for the message sender. */
  senderName: z.string().min(1).optional(),
  /**
   * Optional structured input request rendered as a styled RobotRock action
   * widget below the message text. Use this instead of asking the user to reply
   * in plain text so the request is always a proper action form.
   */
  requestActionInput: z
    .object({
      prompt: z.string().optional(),
      action: agentChatSeedActionSchema,
    })
    .optional(),
});

/**
 * POST /v1/agent-chats body: create + assign one or more agent chat sessions.
 * Either `agentIdentifier` (new chat) or `parentChatId` (spawned from a chat) is required.
 */
const eveAgentChatTransportSchema = z.object({
  kind: z.literal("eve"),
  chatId: z.string().min(1),
  baseUrl: z.string().url(),
  sessionId: z.string().optional(),
  continuationToken: z.string().optional(),
  streamIndex: z.number().int().nonnegative().optional(),
  isStreaming: z.boolean().optional(),
});

export const createAgentChatBodySchema = z
  .object({
    /** Agent id (eve agent name). Required unless `parentChatId` is set. */
    agentIdentifier: z.string().min(1).optional(),
    /** Inherit tenant/connection/agent from an existing chat (agent-spawned chats). */
    parentChatId: z.string().min(1).optional(),
    /** Convex tenantEveConnections id; resolved from the tenant when omitted. */
    eveConnectionId: z.string().min(1).optional(),
    /** Source application id; groups the chat under an inbox section. */
    app: z.string().min(1).optional(),
    /** Email of the user who owns the chat. Required unless `parentChatId` is set. */
    ownerEmail: z.string().email().optional(),
    title: z.string().min(1),
    messages: z.array(agentChatSeedMessageSchema).default([]),
    /** Provenance label stored on the session ("cron" | "agent" | ...). */
    source: z.string().min(1).optional(),
  })
  .refine((data) => Boolean(data.agentIdentifier || data.parentChatId), {
    message: "Provide either agentIdentifier or parentChatId",
  })
  .refine((data) => Boolean(data.ownerEmail || data.parentChatId), {
    message: "Provide ownerEmail unless parentChatId is set",
  });

/** POST /v1/agent-chats/transport body: persist live-chat cursor for the browser. */
export const agentChatTransportBodySchema = eveAgentChatTransportSchema;

/** POST /v1/agent-chats/audit-tool body: log Eve tool executions from agent hooks. */
export const agentChatAuditToolBodySchema = z.object({
  eveSessionId: z.string().min(1),
  userId: z.string().min(1),
  toolCallId: z.string().min(1),
  toolName: z.string().min(1),
  input: z.record(z.string(), z.unknown()).default({}),
  success: z.boolean(),
  status: z.enum(["completed", "failed", "rejected"]),
  error: z.string().optional(),
  idempotencyKey: z.string().optional(),
});

/** POST /v1/agent-chats/audit-input body: log Eve HITL choices from agent hooks. */
export const agentChatAuditInputBodySchema = z.object({
  eveSessionId: z.string().min(1),
  userId: z.string().min(1),
  actionId: z.string().min(1),
  actionTitle: z.string().optional(),
  prompt: z.string().optional(),
  data: z.record(z.string(), z.unknown()).optional(),
  idempotencyKey: z.string().optional(),
  /** Eve input request id — used to merge staged HITL metadata. */
  requestId: z.string().optional(),
  /** Eve tool call id — fallback lookup for staged HITL metadata. */
  toolCallId: z.string().optional(),
});

const eveHitlStagedOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
});

/** POST /v1/agent-chats/stage-hitl body: persist pending Eve HITL requests on the chat. */
export const agentChatStageHitlBodySchema = z.object({
  eveSessionId: z.string().min(1),
  requests: z.array(
    z.object({
      requestId: z.string().min(1),
      toolCallId: z.string().min(1),
      toolName: z.string().min(1),
      prompt: z.string().min(1),
      display: z.enum(["confirmation", "select", "text"]).optional(),
      allowFreeform: z.boolean().optional(),
      options: z.array(eveHitlStagedOptionSchema).optional(),
      toolInput: z.record(z.string(), z.unknown()).optional(),
    })
  ),
});

/** POST /v1/agent-chats/link-task body: associate an inbox task with an Eve chat session. */
export const agentChatLinkTaskBodySchema = z.object({
  eveSessionId: z.string().min(1),
  publicTaskId: z.string().min(1),
  toolCallId: z.string().min(1),
});

/** Where a thread update originated. */
export type ThreadUpdateSource = "api" | "task_create" | "dashboard";

/** Lifecycle status carried by a thread update. */
export type ThreadUpdateStatus = (typeof threadUpdateStatuses)[number];

/** A logged thread update as returned by the API. */
export interface ThreadUpdate {
  id: string;
  threadId: string;
  message: string;
  status: ThreadUpdateStatus;
  source: ThreadUpdateSource;
  createdAt: number;
}

// Type exports
export type AssignToInput = z.infer<typeof assignToSchema>;
export type CreateTaskBodyInput = z.input<typeof createTaskBodySchema>;
export type CreateTaskBody = z.output<typeof createTaskBodySchema>;
export type ThreadUpdateBodyInput = z.input<typeof threadUpdateBodySchema>;
export type ThreadUpdateBody = z.output<typeof threadUpdateBodySchema>;
export type ThreadUpdateInput = z.input<typeof threadUpdateInputSchema>;
export type AgentChatSeedAction = z.output<typeof agentChatSeedActionSchema>;
export type AgentChatSeedMessage = z.output<typeof agentChatSeedMessageSchema>;
export type CreateAgentChatBodyInput = z.input<typeof createAgentChatBodySchema>;
export type CreateAgentChatBody = z.output<typeof createAgentChatBodySchema>;
export type AgentChatTransportBody = z.output<typeof agentChatTransportBodySchema>;
export type AgentChatAuditToolBody = z.output<typeof agentChatAuditToolBodySchema>;
export type AgentChatAuditInputBody = z.output<typeof agentChatAuditInputBodySchema>;
export type AgentChatStageHitlBody = z.output<typeof agentChatStageHitlBodySchema>;
export type AgentChatLinkTaskBody = z.output<typeof agentChatLinkTaskBodySchema>;
export type TaskContextInput = z.input<typeof taskContextSchema>;
export type TaskContextOutput = z.output<typeof taskContextSchema>;
export type TaskContext = TaskContextOutput;
export type SafeUrl = z.infer<typeof safeUrlSchema>;
export type TaskActionInput = z.infer<typeof taskActionInputSchema>;
export type TaskAction = z.infer<typeof taskActionSchema>;
export type WebhookHandler = z.infer<typeof webhookHandlerSchema>;
export type TriggerHandler = z.infer<typeof triggerHandlerSchema>;
export type Handler = z.infer<typeof handlerSchema>;
export type ContextData = z.infer<typeof contextDataSchema>;
export type ContextUiSchema = z.infer<typeof contextUiSchema>;
export type UiFieldSchema = z.infer<typeof uiFieldSchemaSchema>;
export type AgentTelemetry = z.infer<typeof agentTelemetrySchema>;
export type AgentTelemetryInput = z.input<typeof agentTelemetrySchema>;
